import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import moment from 'moment';

/**
 *  BÉRLET és KOMPENZÁCIÓ – dolgozói oldal.
 *
 *  A munkatárs itt adja be a havi bérlet számláját (fájl + összeg), és itt látja,
 *  hol tart az elbírálás. A jóváhagyott tételek összege megy a bér-riport
 *  kompenzációjába – ezért a beadás önmagában még nem jóváírás.
 *
 *  Aki bérügyi jogosultsággal lép be, egy második fülön az egész intézmény
 *  beadványait látja, és itt hagyja jóvá vagy utasítja el őket.
 */
@Component({
  selector: 'app-berlet',
  templateUrl: './berlet.component.html',
  styleUrls: ['./berlet.component.scss'],
})
export class BerletComponent implements OnInit {
  sajatIntezmeny: any;
  user$: any;
  userMetadata: any;
  /*  ELBÍRÁLÓ-E – a KISZOLGÁLÓTÓL kérdezzük, nem itt döntjük el.
   *  Korábban egy kódba írt jogkörlista volt a helyén; a kör innentől az
   *  Adminisztrátori jogosultságok „Bérlet / kompenzáció – elbírálás" területén
   *  szerkeszthető. (A felület elrejtése amúgy sem védelem: a végpontok külön is
   *  ellenőriznek, tehát ez a jelző csak azt dönti el, MIT MUTATUNK.) */
  elbiralo = false;

  ful: 'sajat' | 'elbiralas' = 'sajat';
  ev = moment().year();
  ho = moment().month() + 1;

  sajatTetelek: any[] = [];
  osszesTetel: any[] = [];
  fut = false;

  uj: any = { osszeg: null, megnevezes: 'Havi bérlet', megjegyzes: '' };
  fajl: File = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.user$ = await this.data.getFromStorage('user');
    this.userMetadata = await this.data.getFromStorage('userMetadata');
    await this.jogotKerdez();
    await this.betolt();
  }

  get adid(): string {
    return String(this.userMetadata?.adid || this.user$?.username || '').trim();
  }

  async jogotKerdez() {
    if (!this.sajatIntezmeny?.intezmenyId) return;
    try {
      const j: any = await firstValueFrom(this.http.get(
        `/api/allomany-kompenzacio/${this.sajatIntezmeny.intezmenyId}/jogom`));
      this.elbiralo = !!j?.admin;
    } catch (e) {
      // Ha nem tudjuk megkérdezni, a szűkebb nézet a helyes válasz.
      this.elbiralo = false;
    }
  }

  async betolt() {
    if (!this.sajatIntezmeny?.intezmenyId) return;
    this.fut = true;
    const alap = `/api/allomany-kompenzacio/${this.sajatIntezmeny.intezmenyId}`;
    try {
      /*  Nem adunk át `allomany` szűrőt: aki nem elbíráló, annak a kiszolgáló amúgy is
       *  csak a sajátját adja vissza – az elbíráló pedig itt szándékosan a sajátját kéri. */
      this.sajatTetelek = await firstValueFrom(this.http.get<any[]>(
        `${alap}?allomany=${encodeURIComponent(this.adid)}`));
      if (this.elbiralo) {
        this.osszesTetel = await firstValueFrom(this.http.get<any[]>(`${alap}?ev=${this.ev}&ho=${this.ho}`));
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A lista betöltése nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  fajlValaszt(ev: any) { this.fajl = ev?.target?.files?.[0] || null; }

  async bead() {
    if (!this.uj.osszeg || Number(this.uj.osszeg) <= 0) {
      this.data.sendToast('Add meg a bérlet összegét.', 'warning'); return;
    }
    if (!this.fajl) { this.data.sendToast('Töltsd fel a számlát.', 'warning'); return; }
    const fd = new FormData();
    fd.append('allomany', this.adid);
    fd.append('ev', String(this.ev));
    fd.append('ho', String(this.ho));
    fd.append('tipus', 'berlet');
    fd.append('osszeg', String(this.uj.osszeg));
    fd.append('megnevezes', this.uj.megnevezes || 'Havi bérlet');
    fd.append('megjegyzes', this.uj.megjegyzes || '');
    fd.append('letrehozta', this.adid);
    fd.append('fajl', this.fajl, this.fajl.name);
    this.fut = true;
    try {
      await firstValueFrom(this.http.post(`/api/allomany-kompenzacio/${this.sajatIntezmeny.intezmenyId}`, fd));
      this.data.sendToast('A bérlet beadva – jóváhagyás után írjuk jóvá.', 'success');
      this.uj = { osszeg: null, megnevezes: 'Havi bérlet', megjegyzes: '' };
      this.fajl = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A beadás nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async dontes(tetel: any, dontes: 'jovahagy' | 'elutasit') {
    let indoklas = '';
    if (dontes === 'elutasit') {
      indoklas = String(prompt('Az elutasítás indoka (a munkatárs is látja):') || '').trim();
      if (!indoklas) return;
    }
    try {
      await firstValueFrom(this.http.put(`/api/allomany-kompenzacio/tetel/${tetel.id}/dontes`,
        { dontes, indoklas }));
      this.data.sendToast(dontes === 'jovahagy' ? 'Jóváhagyva.' : 'Elutasítva.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A döntés nem sikerült.', 'error');
    }
  }

  async torol(tetel: any) {
    if (!confirm('Biztosan törlöd ezt a tételt?')) return;
    try {
      await firstValueFrom(this.http.delete(`/api/allomany-kompenzacio/tetel/${tetel.id}`));
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  statuszSzin(s: string) {
    return s === 'jovahagyva' ? 'success' : (s === 'elutasitva' ? 'danger' : 'warning');
  }
  statuszNev(s: string) {
    return s === 'jovahagyva' ? 'Jóváhagyva' : (s === 'elutasitva' ? 'Elutasítva' : 'Elbírálásra vár');
  }
  tipusNev(t: string) {
    return t === 'berlet' ? 'Bérlet' : (t === 'ora' ? 'Óra alapú' : 'Egyéb kompenzáció');
  }
  fajlUrl(t: any) { return t?.fajl ? '/api/file/' + t.fajl : null; }
}
